import { Box, Tooltip, Typography } from '@mui/material';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import React from 'react';
import { useTransactionTooltipData } from '../../../hooks/useTransactionTooltipData.tsx';

interface TransactionProgressProps {
    allTimeTransactionCount: number;
    transactionCount: number;
    planLimit: number;
    selectedYear: string;
}

const TransactionProgress: React.FC<TransactionProgressProps> = ({ 
    allTimeTransactionCount,
    transactionCount, 
    planLimit, 
    selectedYear 
}) => {
    const {
        t,
        theme,
        formattedPlanLimit,
        tooltipContent,
        progressPercentage,
        effectiveTransactionCount
    } = useTransactionTooltipData({ allTimeTransactionCount, transactionCount, planLimit, selectedYear });

    return (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
        }}>
            <Box sx={{ 
                width: { xs: '100%', sm: 300 }, 
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
            }}>
                {effectiveTransactionCount === 0 ? (
                    // Special display for 0 transactions
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'flex-end'
                    }}>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '1rem',
                                color: theme.palette.text.primary
                            }}
                        >
                            {t('taxReports.transactions')} {selectedYear}: 0
                        </Typography>
                    </Box>
                ) : (
                    // Normal progress bar for transactions > 0
                    <Box sx={{ 
                        width: '100%', 
                        height: 32, 
                        bgcolor: 'rgba(0, 0, 0, 0.05)', 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        {/* Filled progress */}
                        <Box sx={{ 
                            width: `${progressPercentage}%`, 
                            bgcolor: theme.palette.primary.main,
                            height: '100%', 
                            borderRadius: 2,
                            transition: 'width 0.3s ease-in-out'
                        }} />
                        
                        {/* Transaction count / limit text */}
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 1
                        }}>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    fontSize: '1rem',
                                    color: progressPercentage > 50 ? '#fff' : theme.palette.text.primary,
                                    textShadow: progressPercentage > 50 ? '0px 0px 2px rgba(0,0,0,0.3)' : 'none',
                                    zIndex: 2,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {effectiveTransactionCount} / {formattedPlanLimit}
                            </Typography>
                        </Box>
                    </Box>
                )}
                
                {/* Info icon with tooltip */}
                <Tooltip 
                    title={tooltipContent}
                    arrow
                    placement="bottom-end"
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                borderRadius: 1,
                                p: 0,
                                maxWidth: 'none',
                                fontSize: '0.9rem',
                                fontWeight: 'bold'
                            }
                        }
                    }}
                >
                    <EqualizerIcon 
                        sx={{
                            ml: 0.5,
                            fontSize: '1.5rem',
                            color: (theme) => theme.palette.text.secondary,
                            cursor: 'help',
                        }} 
                    />
                </Tooltip>
            </Box>
        </Box>
    );
};

export default TransactionProgress;
